// web/src/tutorial/detail/harp-glide.ts
// HarpGlide · 软件界面详细说明（逐个页签、逐个参数）
//
// 写作原则（作者要求）：按截图分区从上到下一块一块讲每个可调参数 ——
// 这个参数管什么、什么时候需要动它、动完会看到什么变化。
//
// 依据：G:\harp_glide_rust
//   src/ui/app.rs（四个页签与顺序、顶栏控件）、avatar_panel.rs（参数设置页的全部滑块与取值范围）、
//   avatar_display.rs（演奏者信息卡片、新增 / 修改 / 删除）、avatar_manager.rs（保存与删除的校验与提示原文）、
//   midi_panel.rs（MIDI 文件卡片、MIDI 信息卡片、音符量化、轨道选择）、
//   poses_reader.rs（Poses 查看器的搜索、筛选、卡片）、execute_panel.rs（配置概览、执行操作、控制台输出）、
//   src/ui/data.rs（全部默认值）、src/main.rs（窗口标题）、
//   assets/i18n/zh-CN.json 与 en-US.json（界面原文，参数名以此为准）

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const harpGlideDetail: TutorialDoc = {
  key: "harp_glide",
  slug: "harp-glide",
  kind: "detail",
  icon: "🎵",
  title: l("HarpGlide 软件界面详细说明", "HarpGlide UI Reference"),
  subtitle: l(
    "按四个页签逐屏逐参数讲清楚：参数控制什么、取值范围和默认值是多少、什么时候需要调整、调整后会看到什么变化。想先跑通一次完整流程，请先看[快速上手](/quickstart/harp-glide.html)。",
    "A tab-by-tab, parameter-by-parameter walkthrough: what each control does, its range and default, when you would touch it, and what changes when you do. To get one run working end to end first, read the [Quick Start](/quickstart/harp-glide.html).",
  ),
  readMinutes: 12,
  video: {
    hidden: true,
    caption: l("本页不内嵌视频", "No embedded video on this page"),
  },
  images: [
    {
      src: "/assets/image/products/harp-glide/CN/参数设置.png",
      srcEn: "/assets/image/products/harp-glide/EN/Settings.png",
      label: l("参数设置", "Settings"),
      // 中英两版窗口大小不同（CN 862×892 / EN 1043×1034），声明取上限
      width: 1043,
      height: 1034,
    },
    {
      src: "/assets/image/products/harp-glide/CN/MIDI信息.png",
      srcEn: "/assets/image/products/harp-glide/EN/MIDI Info'.png",
      label: l("MIDI 信息", "MIDI Info"),
      width: 862,
      height: 892,
    },
    {
      src: "/assets/image/products/harp-glide/CN/Pose查看器.png",
      srcEn: "/assets/image/products/harp-glide/EN/Poses Viewer.png",
      label: l("Poses 查看", "Poses Viewer"),
      width: 862,
      height: 892,
    },
    {
      src: "/assets/image/products/harp-glide/CN/生成动画.png",
      srcEn: "/assets/image/products/harp-glide/EN/Animation.png",
      label: l("生成动画", "Animation"),
      width: 862,
      height: 892,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "how-to-read",
      title: l("这篇怎么用", "How to use this page"),
      blocks: [
        {
          kind: "p",
          text: l(
            "这是一份**参数查询手册**。页面顺序就是软件顶栏页签的顺序：参数设置 → MIDI 信息 → 生成动画，最后加上可选的 Poses 查看。每一节对应一整屏界面，节内的二级标题对应那一屏上的一块卡片，右下角的截图窗口会跟着你滚到哪一节自动换图。",
            "This is a **reference for the controls**. The page follows the app's tab order: 参数设置 / Settings → MIDI 信息 / MIDI Info → 生成动画 / Generate Animation, plus the optional Poses 查看 / Poses Viewer at the end. Each section covers one screen, its sub-sections map to the cards on that screen, and the floating screenshot follows whichever section you are reading.",
          ),
        },
        {
          kind: "p",
          text: l(
            "只想知道怎么最快跑出一份动画，看[快速上手](/quickstart/harp-glide.html)就够了，那里一个参数都不用调。把演奏者人物做成可用角色（.harpist 从哪来、弦数和手位怎么标定）看[自定义演奏者进阶教程](/advanced/harp-glide.html)；插件那一侧的面板看[Blender 插件说明](/manuals/blender/harp-glide.html)。",
            "If you only want the fastest route to a finished animation, the [Quick Start](/quickstart/harp-glide.html) is enough — no parameter needs changing there. For turning your own character into a usable performer (where the .harpist comes from, how string count and hand positions are calibrated), see the [custom performer tutorial](/advanced/harp-glide.html); for the Blender-side panel, see the [plugin manual](/manuals/blender/harp-glide.html).",
          ),
        },
        {
          kind: "note",
          text: l(
            "正文里的按钮名和字段名都与软件界面保持一致。双语切换时文字会变化（例如 参数设置 在英文界面是 Settings），但**位置和参数数量完全一致**，按位置对照就不会错。",
            "Control names in the text match the app UI. Labels change with language (for example, 参数设置 becomes Settings), but **positions and parameter counts are identical**, so navigating by layout remains reliable.",
          ),
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 1 / 参数设置
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-settings",
      title: l("页签 1：参数设置", "Tab 1 — 参数设置 / Settings"),
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏主要决定两件事：**谁来弹**，以及**动作节奏有多紧**。你会在这里选本次生成用的演奏者，并调整动作参数。至于弦数、每根弦的坐标、双手参考姿态、头部三态、竖琴支点这些角色底层数据，都保存在 .harpist 文件里，这一页只负责读取和调用。",
            "This screen decides **who plays** and **how urgently they play**. It does two jobs: choosing the performer for this run, and tuning every motion parameter. The harp's string count, per-string coordinates, hand reference poses, three head states and harp pivots are not here — they live in the performer's .harpist file, and this screen only consumes them.",
          ),
        },
        {
          kind: "p",
          text: l(
            "整屏从上到下三行：第一行左边 基础设置、右边 演奏者信息；第二行整行 技法检测；第三行左边 动作参数、右边 五状态偏移比例。每张卡片左上角的圆形数字是卡片的序号徽章，与实际调节顺序无关。",
            "The screen stacks three rows: 基础设置 / Basic Settings on the left with 演奏者信息 / Avatar Info on the right; the full-width 技法检测 / Technique Detection; then 动作参数 / Motion Parameters on the left with 五状态偏移比例 / Five-State Offset Ratios on the right. The round number in each card's top-left corner is a badge index, not an order you have to follow.",
          ),
        },
      ],
      subs: [
        {
          id: "settings-select-performer",
          title: l(
            "基础设置：选演奏者",
            "基础设置 / Basic Settings — choosing the performer",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "卡片里从上到下只有三样东西：最上面是演奏者下拉菜单，下面一条分隔线，再下面是两个滑块。这三个就是本页签里「不改也能跑」的那部分。",
                "The card holds just three things, top to bottom: the performer dropdown, a divider, and two sliders. These three are the part of this tab you can leave untouched.",
              ),
            },
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("作用", "Effect"),
                l("取值 / 范围", "Range"),
                l("默认", "Default"),
                l("什么时候动它", "When to touch it"),
              ],
              rows: [
                [
                  l("演奏者下拉菜单", "Performer dropdown"),
                  l(
                    "列出软件当前认得的全部演奏者，选中谁就用谁的 .harpist 生成动画。选中后右边的 演奏者信息 卡片立刻跟着换图。",
                    "Lists every performer the app knows about; whichever you pick supplies the .harpist used for generation. The 演奏者信息 / Avatar Info card refreshes immediately.",
                  ),
                  l(
                    "列表内容来自 assets/harpist/avatars.json",
                    "The list comes from assets/harpist/avatars.json",
                  ),
                  l("列表里的第一个", "The first entry"),
                  l(
                    "换角色时。列表里登记的 .harpist 文件被删掉或改名，那一条会被跳过——所以「登记了却没出现」说明文件不在。",
                    "When switching characters. An entry whose .harpist was deleted or renamed is skipped — so “registered but missing from the list” means the file is gone.",
                  ),
                ],
                [
                  l("记录池大小:", "记录池大小: / Pool size:"),
                  l(
                    "指法搜索每一步保留多少个候选方案。调大更容易找到省力、自然的手型，代价是算得慢。这是**最值得动的一个参数**。",
                    "How many candidate fingering plans survive each step of the search. Larger finds more economical, more natural hand shapes but takes longer. This is **the one parameter most worth touching**.",
                  ),
                  l("50 – 1000（滑块）", "50 – 1000 (slider)"),
                  l("400", "400"),
                  l(
                    "手型明显别扭、或者控制台出现「速度过滤淘汰候选」很多时往大调（600–800）；只是想快点出结果就往小调。",
                    "Raise it (600–800) when hand shapes look awkward or the console reports many speed-filtered candidates; lower it when you just want a faster result.",
                  ),
                ],
                [
                  l("目标帧率:", "目标帧率: / Target FPS:"),
                  l(
                    "生成动画的帧率。它同时决定「一秒钟切成多少帧」和音符到帧的换算，所以**改了它等于改了整段动画的时间粒度**。",
                    "The frame rate of the generated animation. It governs both how many frames a second is cut into and the note-to-frame conversion, so **changing it changes the whole animation's time granularity**.",
                  ),
                  l(
                    "24 – 120（滑块，后缀 fps）",
                    "24 – 120 (slider, suffix fps)",
                  ),
                  l("60.0", "60.0"),
                  l(
                    "跟你的目标工程保持一致：30 fps 的项目就填 30，别让插件在导入时再重采样。帧率调低会让快速乐句的细节变粗。",
                    "Match your target project: a 30 fps project should use 30 so the plugin does not resample on import. A lower rate coarsens fast passages.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "info",
              text: l(
                "列表为空时这张卡片不会显示下拉菜单，而是一行「暂无演奏者」加一个居中大按钮 ➕ 新增演奏者；右边的 演奏者信息 卡片则提示「请点击「新增演奏者」按钮创建第一个演奏者」。",
                "With an empty list the card shows no dropdown — just “暂无演奏者 / No avatars yet” and a large centred ➕ 新增演奏者 / New Avatar button, while 演奏者信息 / Avatar Info reads “请点击「新增演奏者 / New Avatar」按钮创建第一个演奏者”.",
              ),
            },
          ],
        },
        {
          id: "settings-avatar-info",
          title: l(
            "演奏者信息：当前演奏者是谁",
            "演奏者信息 / Avatar Info — who is selected",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "右侧这张卡片是「当前演奏者的身份证」：上方一张图片，下方三个按钮。它不参与运算，但它是你确认自己有没有选错角色的唯一凭据——选错演奏者会让整段动画的手位全部偏掉。",
                "The card on the right is the current performer's identity card: an image on top, three buttons below. It takes no part in computation, but it is your only confirmation that the right character is selected — picking the wrong one shifts every hand position in the animation.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "**图片**：就是新增或修改时指定的那张演奏者图片，按比例缩放到最大 600 × 500 显示。没配图片、或图片路径读不到时显示一个 🎵 占位框与「暂无图片」——**这不影响生成**，只是不好认。",
                  "**The image**: the performer picture set when adding or editing, scaled to fit 600 × 500. With no image, or an unreadable path, you get a 🎵 placeholder box reading “暂无图片 / No image” — **harmless for generation**, just harder to recognise.",
                ),
                l(
                  "✏ 修改：把当前演奏者的名称、图片、.harpist 路径填进编辑表单，改完保存即生效。",
                  "✏ 修改 / Edit: loads the current performer's name, image and .harpist path into the edit form; saving applies the change.",
                ),
                l(
                  "➕ 新增：打开一张空表单，创建一个新的演奏者。",
                  "➕ 新增 / Add: opens a blank form to create a new performer.",
                ),
                l(
                  '🗑 删除：弹出 确认删除 窗口，写着「确定要删除演奏者 "…" 吗？」和「此操作不可撤销。」，提供 ❌ 取消 与 ✅ 确认删除。列表里只剩一位演奏者时不允许删除，会提示「不能删除最后一个演奏者」。',
                  '🗑 删除 / Delete: opens a 确认删除 / Confirm Delete window reading “确定要删除演奏者 "…" 吗？” and “此操作不可撤销。” with ❌ 取消 / Cancel and ✅ 确认删除 / Confirm Delete. Deleting is refused when only one performer remains, with the message “不能删除最后一个演奏者” / “Cannot delete the last avatar”.',
                ),
              ],
            },
          ],
        },
        {
          id: "settings-avatar-edit",
          title: l(
            "新增 / 修改演奏者的表单与报错",
            "The add / edit form and its errors",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "点 ✏ 修改 或 ➕ 新增 后，演奏者信息 卡片会被编辑表单替换掉，表单标题相应变成 修改演奏者 或 新增演奏者。三项输入加两个按钮：",
                "After ✏ 修改 / Edit or ➕ 新增 / Add the 演奏者信息 / Avatar Info card is replaced by the edit form, titled 修改演奏者 / Edit Avatar or 新增演奏者 / New Avatar. Three inputs and two buttons:",
              ),
            },
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("作用", "Effect"),
                l("要点", "Key point"),
              ],
              rows: [
                [
                  l("演奏者名称:", "演奏者名称: / Avatar name:"),
                  l(
                    "文本输入框，就是下拉菜单里显示的名字。",
                    "A text field — the name shown in the dropdown.",
                  ),
                  l(
                    "不能为空，也不能和列表里另一个演奏者重名（改自己不算重名）。",
                    "Cannot be empty, and cannot duplicate another entry in the list (editing the same entry is not a duplicate).",
                  ),
                ],
                [
                  l("演奏者图片:", "演奏者图片: / Avatar image:"),
                  l(
                    "点 📂 选择图片 挑一张图片，文件对话框的过滤项是「图片文件」，接受 .png / .jpg / .jpeg。",
                    "Press 📂 选择图片 / Select Image to pick a picture; the dialog filters on “图片文件 / Image files” and accepts .png / .jpg / .jpeg.",
                  ),
                  l(
                    "**可以留空**：只有名称和 .harpist 的演奏者也是合法的，只是界面上显示 🎵 占位图。",
                    "**Can be left empty**: a performer with only a name and a .harpist is valid, showing the 🎵 placeholder.",
                  ),
                ],
                [
                  l("配置文件:", "配置文件: / File:"),
                  l(
                    "点 📂 选择 .harpist 文件 指定这个演奏者的角色数据文件（弦数、手位、姿势全在里面）。",
                    "Press 📂 选择 .harpist 文件 / Select .harpist File to set the performer's data file (string count, hand positions and poses all live in it).",
                  ),
                  l(
                    "**必填**：不选这个文件直接保存会被拒绝。",
                    "**Required**: saving without it is refused.",
                  ),
                ],
                [
                  l("💾 保存 / ❌ 取消", "💾 保存 / Save / ❌ 取消 / Cancel"),
                  l(
                    "保存会把选中的图片与 .harpist 复制到软件的 assets/harpist/ 目录，并重写 avatars.json，所以下次启动还能看到；取消则丢弃这次的编辑。",
                    "Saving copies the chosen image and .harpist into the app's assets/harpist/ folder and rewrites avatars.json, so the performer survives a restart. Cancel discards the edit.",
                  ),
                  l(
                    "保存失败时表单底部弹出一条红色横幅，格式是「错误: …」。",
                    "On failure a red banner appears at the bottom of the form, reading “错误: …”.",
                  ),
                ],
              ],
            },
            {
              kind: "table",
              head: [
                l("红色横幅原文", "The red banner text"),
                l("触发条件", "Trigger"),
              ],
              rows: [
                [
                  l("演奏者名称不能为空", "演奏者名称不能为空"),
                  l(
                    "没有填 演奏者名称: 就点了保存。",
                    "Save was pressed with an empty 演奏者名称: / Avatar name:.",
                  ),
                ],
                [
                  l("演奏者名称已存在", "演奏者名称已存在"),
                  l(
                    "新名称和列表里另一位演奏者重名。",
                    "The new name duplicates another performer in the list.",
                  ),
                ],
                [
                  l(
                    "必须选择演奏者配置文件(.harpist)",
                    "必须选择演奏者配置文件(.harpist)",
                  ),
                  l(
                    "没有通过 📂 选择 .harpist 文件 指定配置文件。",
                    "No file was chosen via 📂 选择 .harpist 文件 / Select .harpist File.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "settings-technique",
          title: l(
            "技法检测：一个勾选框",
            "技法检测 / Technique Detection — a single checkbox",
          ),
          blocks: [
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("作用", "Effect"),
                l("取值", "Values"),
                l("默认", "Default"),
                l("什么时候动它", "When to touch it"),
              ],
              rows: [
                [
                  l(
                    "启用滑奏检测",
                    "启用滑奏检测 / Enable glissando detection",
                  ),
                  l(
                    "识别滑奏（glissando）技法。打开之后，滑奏段落里被漏掉的音会被补全，无法演奏的音符会被过滤掉，滑奏的伴奏音符也会重新收集——所以它改变的不只是识别结果，而是**音符本身**。",
                    "Detects glissando. With it on, notes missed inside a glissando are filled in, unplayable notes are filtered out, and glissando accompaniment notes are re-collected — so it changes **the notes themselves**, not just the labelling.",
                  ),
                  l("勾选 / 不勾选", "On / off"),
                  l("勾选（开启）", "On"),
                  l(
                    "曲子里有刮奏就保持开启。如果某段滑奏被判错、听起来少了音或多出一串动作，可以临时取消勾选对比一次。",
                    "Keep it on for pieces with glissandi. If one passage is misjudged — notes missing, or an extra run of motion — untick it and run once to compare.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "settings-motion-params",
          title: l(
            "动作参数：七个滑块决定演奏手感",
            "动作参数 / Motion Parameters — seven sliders set the feel",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这是全页签最需要理解的一组参数。七个滑块从上到下依次是：拨弦快慢、手移动速度上限、琶音判定、踏板移动快慢、过渡时长，以及两个「敏感度」。默认值是作者调过的通用配置，第一次用建议先按默认跑一遍，看到具体问题再回来改这里的某一项。",
                "This is the group worth understanding. The seven sliders run top to bottom: pluck speed, hand-speed ceiling, arpeggio detection, pedal travel time, transition length, and two sensitivities. The defaults are the author's general-purpose setup — run once with them, then come back and change one specific slider when you see a specific problem.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "Effect"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调、调完看到什么", "How to tune, and what changes"),
              ],
              rows: [
                [
                  l(
                    "拨弦攻击时间 (秒)",
                    "拨弦攻击时间 (秒) / Pluck attack time (s)",
                  ),
                  l(
                    "一根手指从接触琴弦到完成拨弦花多长时间。它决定单次拨弦的「干脆度」：短了像拨片，长了像指腹抚过。",
                    "How long a finger takes from touching a string to finishing the pluck. It sets how crisp a single pluck reads: short feels like a plectrum, long like a fingertip brushing past.",
                  ),
                  l("0.01 – 0.3", "0.01 – 0.3"),
                  l("0.100", "0.100"),
                  l(
                    "快速乐段显得拖沓就往下调（0.05 左右）；想要柔和、绵密的拨弦就往上调。调到很小的时候手指动作会变得很「抽」，可以先看 Poses 查看 确认。",
                    "Lower it (around 0.05) when fast passages drag; raise it for a softer, denser pluck. At very small values the fingers look twitchy — check in Poses 查看 / Poses Viewer first.",
                  ),
                ],
                [
                  l(
                    "手部移动速度 (弦/秒)",
                    "手部移动速度 (弦/秒) / Hand move speed (strings/s)",
                  ),
                  l(
                    "手掌在弦面上移动的速度上限，单位是「每秒能跨过多少根弦」。搜索阶段用它淘汰「来不及移动到目标弦」的候选方案，所以它既影响手速也影响能否算出解。",
                    "The palm's speed ceiling across the strings, in strings per second. The search uses it to discard candidates that could not physically reach the target string, so it affects both how fast the hands look and whether a solution exists at all.",
                  ),
                  l("1.0 – 60.0", "1.0 – 60.0"),
                  l("10.0", "10.0"),
                  l(
                    "**这是「手速上限」**。调大：允许更快移动、被速度过滤掉的帧更少，但手看起来更忙；调小：手更从容，但候选被淘汰得多，甚至算不出方案。控制台里「速度超标跳过帧数」「池子变空次数」偏高时，先调大这一个。",
                    "**This is the hand-speed ceiling.** Raise it to allow faster travel and fewer speed-filtered frames (at the cost of busier-looking hands); lower it for calmer hands, at the risk of discarding so many candidates that no plan survives. When the console shows high speed-filtered or pool-empty counts, raise this one first.",
                  ),
                ],
                [
                  l(
                    "琶音检测阈值 (秒)",
                    "琶音检测阈值 (秒) / Arpeggio detection threshold (s)",
                  ),
                  l(
                    "相邻两个音的时间间隔小于这个值，就按「琶音」而不是「和弦」处理，指法分配方式随之不同。",
                    "Notes closer together than this value are treated as an arpeggio rather than a chord, which changes how the fingering is assigned.",
                  ),
                  l("0.0 – 1.0", "0.0 – 1.0"),
                  l("0.250", "0.250"),
                  l(
                    "分解和弦被当成同时按下的和弦、手型因此别扭时可以动它：想更容易判成琶音就调大，想让它当和弦处理就调小。多数曲子保持默认。",
                    "Worth touching when a broken chord is read as a block chord and the hand shape suffers: raise it to favour arpeggio detection, lower it to favour chords. Most pieces stay at the default.",
                  ),
                ],
                [
                  l(
                    "踏板切换时间 (秒)",
                    "踏板切换时间 (秒) / Pedal switch time (s)",
                  ),
                  l(
                    "竖琴的脚从当前踏板档位移动到目标档位需要多长时间。七个踏板各自有多个档位，转调时脚要在档位之间移动。",
                    "How long a foot takes to travel from one pedal position to the next. Each of the seven pedals has several positions, and modulating passages move the feet between them.",
                  ),
                  l("0.05 – 1.0", "0.05 – 1.0"),
                  l("0.150", "0.150"),
                  l(
                    "转调密集、脚踏动作看起来赶不过来就调大；脚的动作明显慢半拍、跟不上乐句就调小。它只影响脚和踏板的动画，不改变手的部分。",
                    "Raise it when dense key changes make the feet look rushed; lower it when pedal motion visibly lags the phrase. It only affects the feet and pedals, not the hands.",
                  ),
                ],
                [
                  l(
                    "最大过渡持续时间 (秒)",
                    "最大过渡持续时间 (秒) / Max transition duration (s)",
                  ),
                  l(
                    "两个音相隔较远时，允许从「保持」阶段挪用多长时间来做过渡动作，让换位慢一点、自然一点。",
                    "When two notes are far apart, how much time may be borrowed from the hold phase for the transition, so the hand shift is slower and more natural.",
                  ),
                  l("0.5 – 10.0", "0.5 – 10.0"),
                  l("3.00", "3.00"),
                  l(
                    "间奏、长休止处手显得很空就调大（过渡被拉长）；换位拖泥带水就调小。",
                    "Raise it when long rests leave the hands looking idle (transitions stretch out); lower it when hand shifts drag.",
                  ),
                ],
                [
                  l(
                    "转向敏感度 (%)",
                    "转向敏感度 (%) / Turning sensitivity (%)",
                  ),
                  l(
                    "竖琴本体跟随左手位置变化的灵敏度。100% 时每帧都重算竖琴的位置与朝向；低于 100% 时，左手位置变化要超过 (1 − 敏感度) × 左手近端值 才重算一次。",
                    "How readily the harp body follows the left hand. At 100% the harp's position and orientation are recomputed every frame; below that, the left hand must move more than (1 − sensitivity) × left-hand near value to trigger a recompute.",
                  ),
                  l(
                    "0.0 – 1.0（界面显示 0–100%）",
                    "0.0 – 1.0 (shown as 0–100%)",
                  ),
                  l("0.900（90%）", "0.900 (90%)"),
                  l(
                    "竖琴跟着手一起抖、晃动太频繁就调低；竖琴迟迟不跟手、看起来像钉在原地就调高。接近 100% 时琴体会很「黏」手，但计算量也最大。",
                    "Lower it when the harp jitters along with the hand; raise it when the harp lags and looks pinned in place. Near 100% the body sticks to the hand closely, at the highest computation cost.",
                  ),
                ],
                [
                  l(
                    "头部运动敏感度 (%)",
                    "头部运动敏感度 (%) / Head motion sensitivity (%)",
                  ),
                  l(
                    "头部朝向依据的 Mid_Hand 的中点位置的记录灵敏度。100% 时手一动就记一个头部关键帧；低于 100% 时，中点要移动超过 (1 − 敏感度) × 双脚距离 才记录。",
                    "Recording sensitivity for the Mid_Hand midpoint that drives head orientation. At 100% every hand movement records a head keyframe; below that, the midpoint must move more than (1 − sensitivity) × feet distance.",
                  ),
                  l(
                    "0.0 – 1.0（界面显示 0–100%）",
                    "0.0 – 1.0 (shown as 0–100%)",
                  ),
                  l("0.950（95%）", "0.950 (95%)"),
                  l(
                    "头部动作显得抖、曲线毛躁就调低；头完全不跟手、一段音乐里几乎不动就调高。想知道实际用的阈值是多少，可以看运行日志里那行 双脚距离=… , Mid_Hand敏感度=…。",
                    "Lower it when the head looks jittery or its curve is noisy; raise it when the head barely moves. To see the threshold actually in use, read the 双脚距离=… , Mid_Hand敏感度=… line in the run log.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "settings-offsets",
          title: l(
            "五状态偏移比例：四个千分比滑块",
            "五状态偏移比例 / Five-State Offset Ratios — four per-mille sliders",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "竖琴拨弦不是「碰到弦就结束」：手指要先摆到弦前方（Ready），顺着拨弦方向推过去（Hold Start → Hold End），再沿按压方向离开（Release）。这块卡片控制的就是这四个阶段各自的**偏移量比例**。",
                "A harp pluck is not over the moment the finger touches the string: the hand positions in front of it (Ready), travels along the pluck direction (Hold Start → Hold End), then leaves along the press direction (Release). This card controls the **offset ratio** for each of those four stages.",
              ),
            },
            {
              kind: "callout",
              tone: "info",
              title: l("它是比例，不是距离", "Ratios, not distances"),
              text: l(
                "卡片标题下写着原文：「偏移比例 × clamp(time_interval, 0, 2) / 2，参考 0 号弦长度」。意思是滑块上的数字不是绝对距离，而是**按音符时长自动缩放的比例**：实际偏移 = 比例 × clamp(音符时长, 0, 2) / 2，再乘以 0 号弦的长度。所以长音偏移大、短音偏移小，你调的是「相对这个音符有多深」而不是「多少厘米」。滑块值以千分比（‰）显示，内部存的默认值 0.005 显示为 5.0‰。",
                "Under the card title the app prints: “偏移比例 × clamp(time_interval, 0, 2) / 2，参考 0 号弦长度”. The slider numbers are not absolute distances but **ratios that scale with note duration**: offset = ratio × clamp(note duration, 0, 2) / 2, multiplied by the length of string 0. Long notes offset more, short notes less — you are setting depth relative to the note, not a fixed distance. Sliders display per-mille values, so the stored default 0.005 reads as 5.0‰.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "Effect"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune"),
              ],
              rows: [
                [
                  l("Ready 偏移", "Ready 偏移"),
                  l(
                    "准备阶段：手掌与手指沿**拨弦反方向**退开多少，相当于起手的预备量。",
                    "Ready stage: how far the palm and fingers pull back **against the pluck direction** — the wind-up.",
                  ),
                  l("0.0 – 20.0‰", "0.0 – 20.0‰"),
                  l("5.0‰", "5.0‰"),
                  l(
                    "手指看起来「贴」在弦上、没有预备动作就调大；预备动作夸张、像在弦前犹疑就调小。",
                    "Raise it when fingers look glued to the string with no wind-up; lower it when the wind-up looks exaggerated, as if hesitating in front of the string.",
                  ),
                ],
                [
                  l("Hold Start 偏移", "Hold Start 偏移"),
                  l(
                    "保持阶段起点：沿**拨弦方向**偏移多少，也就是手指压进弦的程度。",
                    "Hold start: how far the hand offsets **along the pluck direction** — how deeply the finger presses into the string.",
                  ),
                  l("0.0 – 20.0‰", "0.0 – 20.0‰"),
                  l("3.0‰", "3.0‰"),
                  l(
                    "弦振动的幅度看起来太小、拨弦没有「吃进弦里」的感觉就调大。",
                    "Raise it when the string vibration looks shallow and the pluck never seems to bite into the string.",
                  ),
                ],
                [
                  l("Hold End 偏移", "Hold End 偏移"),
                  l(
                    "保持阶段终点：仍沿**拨弦方向**，手指在弦上的最远位置。",
                    "Hold end: still **along the pluck direction** — the finger's furthest position on the string.",
                  ),
                  l("0.0 – 20.0‰", "0.0 – 20.0‰"),
                  l("4.0‰", "4.0‰"),
                  l(
                    "与 Hold Start 偏移 一起决定「保持」这一段手指走了多远。两者差得太小会让拨弦显得短促。",
                    "Together with Hold Start 偏移 it sets how far the finger travels during the hold. Too small a gap between the two makes the pluck feel clipped.",
                  ),
                ],
                [
                  l("Release 偏移", "Release 偏移"),
                  l(
                    "释放阶段：沿**按压方向**离开多少，也就是拨完抽手的幅度。它是四个里默认最大的一项。",
                    "Release stage: how far the hand withdraws **along the press direction** — the follow-through. It has the largest default of the four.",
                  ),
                  l("0.0 – 20.0‰", "0.0 – 20.0‰"),
                  l("10.0‰", "10.0‰"),
                  l(
                    "拨完手指还粘在弦上、没有收回动作就调大；抽手动作太大、看着像在挥手就调小。",
                    "Raise it when fingers stay stuck on the string after the pluck; lower it when the follow-through is so large it looks like a wave.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              text: l(
                "这四项都属于「微调」：它们只改变手指在弦附近的毫米级偏移，不动手指落在哪根弦、也不动手在弦面上的位置。如果问题是「手指被分到了错误的弦」或「手整体偏到琴体另一侧」，改这里没有用——那是 .harpist 里的手位与坐标系问题。",
                "These four are fine-tuning: they only shift the fingers' millimetre-scale offset near the string. They do not change which string a finger lands on, nor where the hand sits. If the problem is “the finger was assigned the wrong string” or “the hands sit on the far side of the harp”, this card will not fix it — that is the hand positions and coordinate system in the .harpist.",
              ),
            },
          ],
        },
      ],
      image: { image: 0 },
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 2 / MIDI 信息
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-midi",
      title: l("页签 2：MIDI 信息", "Tab 2 — MIDI 信息 / MIDI Info"),
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏回答两个问题：**用哪份 MIDI**、**给哪些轨道生成动画**。左栏从上到下是 MIDI 文件、扫描后才会出现的只读 MIDI 信息 卡片，以及 参数设置；右栏是 轨道选择。",
            "This screen answers two questions: **which MIDI** and **which tracks get animated**. The left column stacks MIDI 文件, the read-only MIDI 信息 / MIDI Info card that only appears after scanning, and 参数设置 / Settings; the right column holds 轨道选择 / Track Selection.",
          ),
        },
      ],
      subs: [
        {
          id: "midi-file",
          title: l(
            "MIDI 文件：选文件与扫描",
            "MIDI 文件 — loading and scanning",
          ),
          blocks: [
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("作用", "Effect"),
                l("点了会发生什么", "What happens"),
              ],
              rows: [
                [
                  l("当前文件:", "当前文件: / Current file:"),
                  l(
                    "只读显示已选中的文件名（青色），下面一行小字是完整路径。没选文件时这块位置显示空状态「未选择 MIDI 文件 / 请选择一个 .mid 或 .midi 文件」。",
                    "Read-only display of the chosen file name (in the accent colour) with the full path in small text beneath. With nothing chosen the area shows the empty state “未选择 / Select MIDI 文件 / No MIDI file selected” / “请选择一个 .mid 或 .midi 文件”.",
                  ),
                  l("没有按钮，只是回显。", "No button — display only."),
                ],
                [
                  l(
                    "📂 选择 MIDI 文件",
                    "📂 选择 MIDI 文件 / Select MIDI File",
                  ),
                  l(
                    "打开文件对话框挑一份 MIDI，过滤项是「MIDI 文件」，只认 .mid / .midi。",
                    "Opens a file dialog for a MIDI file, filtered on “MIDI 文件” and accepting only .mid / .midi.",
                  ),
                  l(
                    "选中后立刻替换当前文件，并**清空上一次的扫描结果**（轨道表和速度信息一起清掉），必须重新扫描。",
                    "Selecting replaces the current file immediately and **clears the previous scan** (track list and tempo info together), so you must scan again.",
                  ),
                ],
                [
                  l("🔍 扫描轨道信息", "🔍 扫描轨道信息 / Scan Track Info"),
                  l(
                    "解析这份 MIDI，把含音符的轨道列到右栏。",
                    "Parses the MIDI and lists the tracks containing notes in the right column.",
                  ),
                  l(
                    "成功时控制台写下「MIDI 扫描完成，共 {} 条有效轨道」，同时把 Ticks/Beat、速度变化、起始 BPM 填进下面那张 MIDI 信息 卡片。",
                    "On success the console logs “MIDI 扫描完成，共 {} 条有效轨道” and fills Ticks/Beat, tempo changes and 起始 BPM / Start BPM into the MIDI 信息 / MIDI Info card below.",
                  ),
                ],
                [
                  l(
                    "📥 将 MIDI 文件拖放到此处",
                    "📥 将 MIDI 文件拖放到此处 / Drop a MIDI file here",
                  ),
                  l(
                    "虚线拖放区，副标题是「支持 .mid 和 .midi 格式」。把文件从资源管理器拖进窗口任意位置都算拖放，不必对准这块区域。",
                    "The dashed drop zone, subtitled “支持 .mid 和 .midi 格式”. Dropping anywhere in the window counts — you do not have to aim at this area.",
                  ),
                  l(
                    "扩展名是 .mid / .midi 就替换当前文件并清空扫描结果；否则弹出红色横幅且**当前文件不变**。",
                    "A .mid / .midi extension replaces the current file and clears the scan; anything else raises a red banner and **leaves the current file untouched**.",
                  ),
                ],
              ],
            },
            {
              kind: "table",
              head: [
                l("底部红色横幅", "Red banner at the bottom"),
                l("触发条件", "Trigger"),
              ],
              rows: [
                [
                  l(
                    "错误：请先选择 MIDI 文件",
                    "错误：请先选择 MIDI 文件 / Select MIDI File",
                  ),
                  l(
                    "还没选文件就点了 🔍 扫描轨道信息。",
                    "🔍 扫描轨道信息 / Scan Track Info was pressed with no file chosen.",
                  ),
                ],
                [
                  l(
                    "错误：文件扩展名不正确，期望 .mid 或 .midi，实际为 .xxx",
                    "错误：文件扩展名不正确，期望 .mid 或 .midi，实际为 .xxx",
                  ),
                  l(
                    "拖进来的文件不是 MIDI。",
                    "The dropped file is not a MIDI.",
                  ),
                ],
                [
                  l("错误：拖放的文件没有扩展名", "错误：拖放的文件没有扩展名"),
                  l(
                    "拖进来的文件完全没有扩展名。",
                    "The dropped file has no extension at all.",
                  ),
                ],
                [
                  l("无法加载 MIDI 文件：{原因}", "无法加载 MIDI 文件：{原因}"),
                  l(
                    "文件能选中但解析失败（损坏、格式异常等），后面的原因就是具体报错。",
                    "The file was selectable but parsing failed (corrupt or malformed); the reason follows.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "横幅会在约 4 秒后自动消失，离开这一屏再回来也会清掉——它只是一次性提示，不是需要你处理的状态。",
                "Banners disappear on their own after about four seconds, and also clear when you leave and return to the screen — they are one-shot notices, not states you must clear.",
              ),
            },
          ],
        },
        {
          id: "midi-info-card",
          title: l(
            "MIDI 信息：只读的三个数",
            "MIDI 信息 / MIDI Info — three read-only numbers",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "扫描成功后，左栏会多出一张只读小卡片，三个数值全部来自文件本身，**没有可调项**。它的用途是让你确认「选对了文件」以及「这份文件的时间基准是什么」。",
                "After a successful scan a small read-only card appears in the left column. All three values come from the file itself and **nothing here is adjustable**. Its purpose is confirming you picked the right file and knowing its time base.",
              ),
            },
            {
              kind: "table",
              head: [
                l("显示项", "Field"),
                l("含义", "Meaning"),
                l("什么时候看它", "When it matters"),
              ],
              rows: [
                [
                  l("Ticks/Beat", "Ticks/Beat"),
                  l(
                    "这份 MIDI 每拍有多少个 tick，程序和帧换算时要用。软件内置的初始值是 480。",
                    "How many ticks per beat this MIDI uses, needed for the frame conversion. The app's built-in initial value is 480.",
                  ),
                  l(
                    "帧号与原始 tick 对不上时，先看这里确认真实值。",
                    "When frame numbers and raw ticks seem to disagree, check here for the real value.",
                  ),
                ],
                [
                  l("速度变化段数", "速度变化段数 / Tempo change segments"),
                  l(
                    "这份文件里有多少段不同的速度（tempo）。数字大说明曲子渐快渐慢很多。",
                    "How many distinct tempo segments the file contains. A large number means lots of accelerando and ritardando.",
                  ),
                  l(
                    "段数很多而动作显得毛躁时，可以理解为速度本身在频繁变化，不一定是参数问题。",
                    "When the segment count is high and the motion looks jittery, remember the tempo itself keeps changing — it may not be a parameter problem.",
                  ),
                ],
                [
                  l("起始 BPM", "起始 BPM / Start BPM"),
                  l(
                    "第一段速度换算出的每分钟拍数。",
                    "Beats per minute derived from the first tempo segment.",
                  ),
                  l(
                    "最快的核对方式：和你谱面上标注的速度对一下，确认选对了曲子。",
                    "The quickest sanity check: compare it against the tempo marked on your score to confirm you picked the right piece.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "info",
              text: l(
                "**这张卡片不是每次都出现。** 它只在扫描出速度变化、或 Ticks/Beat 不等于内置初始值 480 时才显示。一份完全规整、每拍 480 tick 的 MIDI 扫描完看不到它，这是正常的，不代表扫描失败。",
                "**This card does not always appear.** It is shown only when the scan found tempo changes, or when Ticks/Beat differs from the built-in initial value 480. A perfectly regular file at 480 ticks per beat will not show it — that is normal and does not mean the scan failed.",
              ),
            },
          ],
        },
        {
          id: "midi-quantization",
          title: l(
            "参数设置：音符量化",
            "参数设置 / Settings — note quantization",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "左栏最底部的 参数设置 卡片里只有一个可调项。它是本页签唯一会影响生成结果的参数。",
                "The 参数设置 / Settings card at the bottom of the left column holds exactly one adjustable item — the only parameter on this tab that affects the result.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "Effect"),
                l("可选值", "Options"),
                l("默认", "Default"),
                l("怎么调", "How to tune"),
              ],
              rows: [
                [
                  l("音符量化", "音符量化 / Note Quantization"),
                  l(
                    "把时间上相近的音符归到同一个格子上，让音符更规整。下拉框下面那条横幅写的就是「对相近的音符进行分组，使音符更规整。」",
                    "Snaps notes that are close in time onto the same grid, making them more regular. The banner under the dropdown reads “对相近的音符进行分组，使音符更规整。” / “Groups nearby notes together to make the notes more regular.”",
                  ),
                  l(
                    "无量化 / 1/16 / 1/32 / 1/64 / 1/128",
                    "无量化 / None / 1/16 / 1/32 / 1/64 / 1/128",
                  ),
                  l("无量化", "无量化 / None"),
                  l(
                    "真人弹奏的 MIDI 时间点常有几十毫秒的抖动，用 1/16 或 1/32 通常能让手型干净很多；写谱软件导出的 MIDI 本来就对齐，保持 无量化。注意量化会**改变音符落点**，改完建议重新生成一遍对比手型。",
                    "Human-played MIDI often jitters by tens of milliseconds, so 1/16 or 1/32 usually cleans the hand shapes up noticeably; notation-exported MIDI is already aligned and can stay at 无量化 / None. Quantization **shifts note timings**, so regenerate once and compare the hand shapes.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "midi-tracks",
          title: l(
            "轨道选择：决定生成哪些轨",
            "轨道选择 / Track Selection — which tracks get animated",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "扫描完成后，右栏列出这份 MIDI 里**所有含音符的轨道**——音符数为 0 的轨道不会出现。卡片副标题实时显示「已选 N / M 条轨道」，勾选框的任何变化立刻反映在这里，也立刻成为生成时的实际输入。",
                "After scanning, the right column lists **every track that contains notes** — tracks with zero notes are hidden. The card subtitle shows “已选 / selected N / M 条轨道” / “{} / {} tracks selected” live, and any checkbox change is immediately reflected there and immediately becomes the real input for generation.",
              ),
            },
            {
              kind: "table",
              head: [
                l("行内元素", "Row element"),
                l("含义", "Meaning"),
                l("怎么用", "How to use it"),
              ],
              rows: [
                [
                  l("最左侧勾选框", "Leftmost checkbox"),
                  l(
                    "这条轨是否参与本次生成。",
                    "Whether this track takes part in the run.",
                  ),
                  l(
                    "只勾真正要演奏的轨。竖琴曲一般只勾竖琴轨，伴奏与打击轨不要勾。",
                    "Tick only the tracks that should be performed. A harp piece usually needs just the harp track; leave accompaniment and percussion unticked.",
                  ),
                ],
                [
                  l("轨道 N", "轨道 N"),
                  l(
                    "轨道序号，出现在执行页的 选中轨道 回显、输出文件名与 Poses 查看 的数据来源里。",
                    "The track index, shown in the execution page's 选中轨道 / Selected tracks echo, in output file names, and in where Poses 查看 / Poses Viewer reads from.",
                  ),
                  l(
                    "记下来，方便在 Poses 查看 和输出文件名之间对照。",
                    "Note it down for cross-checking between Poses 查看 / Poses Viewer and the output file names.",
                  ),
                ],
                [
                  l("Ch{通道号}", "Ch{channel}"),
                  l(
                    "该轨使用的 MIDI 通道。轨道没有通道信息时这一段整个不显示。",
                    "The track's MIDI channel. When the track carries no channel, this segment is omitted entirely.",
                  ),
                  l(
                    "共用同一通道的多条轨通常属于同一件乐器，可以用来判断该勾哪几条。",
                    "Tracks sharing one channel usually belong to the same instrument — useful when deciding what to tick.",
                  ),
                ],
                [
                  l("名称或乐器名", "Name or instrument"),
                  l(
                    "优先显示轨道名；轨道名为空时显示程序推断出的乐器名。",
                    "Shows the track name, falling back to the inferred instrument name when the track has none.",
                  ),
                  l(
                    "用来确认这条轨是不是竖琴 / 钢琴，避免勾错。",
                    "Confirms whether this really is the harp / piano track and avoids ticking the wrong one.",
                  ),
                ],
                [
                  l("(N 个音符)", "(N notes)"),
                  l("该轨的音符总数。", "The track's total note count."),
                  l(
                    "数字异常小通常说明选错了轨；整个列表为空说明这份 MIDI 没有含音符的轨道，需要换文件。",
                    "An unusually small number usually means the wrong track; a completely empty list means the MIDI has no tracks with notes and you need another file.",
                  ),
                ],
                [
                  l(
                    "全选 / 全不选",
                    "全选 / Select All / 全不选 / Deselect All",
                  ),
                  l(
                    "两个作用于整张表的按钮：一个把所有已扫描到的轨全部勾上，一个把全部勾选清掉。",
                    "Two bulk buttons over the whole table: one ticks every scanned track, the other clears every selection.",
                  ),
                  l(
                    "「先全勾再取消几条」或「换曲子回来先清干净」时最快。",
                    "Fastest for “tick everything then untick a few”, or for starting clean after switching pieces.",
                  ),
                ],
                [
                  l(
                    "右侧 🎵 泛音 勾选框",
                    "Right-hand 🎵 泛音 / Harmonic checkbox",
                  ),
                  l(
                    "把这条轨标记为**泛音轨**：该轨的音符按泛音技法处理，而不是普通拨弦。",
                    "Marks the track as a **harmonic track**: its notes are handled as harmonics rather than ordinary plucks.",
                  ),
                  l(
                    "只在整条轨确实是泛音声部时才勾。勾上之后执行页的 配置概览 会多出一行 泛音音轨。",
                    "Tick it only when the whole track really is a harmonic part. Doing so adds a 泛音音轨 / Harmonic tracks row to the execution page's 配置概览 / Configuration Overview.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "info",
              text: l(
                "这一屏**没有**「修剪音符」「速度均一化」这类 MIDI 预处理按钮，程序也不会改写你的 MIDI 文件。素材里有断掉的长音或需要抹平的渐快渐慢，请先在外部 MIDI 编辑器里处理好，再回到这里重新选文件、重新扫描。",
                "This screen has **no** MIDI preprocessing buttons such as trimming notes or normalizing tempo, and the app never rewrites your MIDI. If the material has fragmented long notes or tempo drift to flatten, fix it in an external MIDI editor first, then come back, re-select the file and scan again.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              text: l(
                "轨道表为空时卡片显示「暂无轨道数据 / 请先选择 MIDI 文件并点击「扫描轨道信息」」。一个轨道都没勾就点 ⚡ 生成动画，控制台只会得到一条 错误: 未选择任何音轨。",
                "With an empty track list the card reads “暂无轨道数据” / “No track data yet”, and “请先选择 MIDI 文件并点击「扫描轨道信息」” / “Select a MIDI file and click Scan Track Info”. Pressing ⚡ 生成动画 / Generate Animation with nothing ticked only produces 错误: 未选择任何音轨 / Error: no track selected in the console.",
              ),
            },
          ],
        },
      ],
      image: { image: 1 },
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 3 / 生成动画
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-execute",
      title: l("页签 3：生成动画", "Tab 3 — 生成动画 / Generate Animation"),
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏是执行页，**没有任何输入项**：左栏两张卡片是只读的配置回显与执行按钮，右栏是控制台。演奏者、轨道、参数全都要在别的页签里改好，这里只负责跑。",
            "This is the execution screen and it holds **no inputs at all**: the two cards on the left are a read-only configuration echo plus the run button, and the console sits on the right. Performer, tracks and parameters must all be set on other tabs; this one only runs.",
          ),
        },
      ],
      subs: [
        {
          id: "execute-overview",
          title: l(
            "配置概览：动手前先核对",
            "配置概览 / Configuration Overview — check before running",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "卡片自上而下三块：当前演奏者、当前 MIDI，然后是一张小表格。缺东西时对应的块会换成提示而不是数字。",
                "The card has three blocks top to bottom: 当前演奏者 / Current Avatar, 当前 MIDI / Current MIDI, then a small grid. When something is missing, its block is replaced by a hint instead of a value.",
              ),
            },
            {
              kind: "table",
              head: [
                l("显示项", "Field"),
                l("内容", "Content"),
                l("缺失时显示", "When missing"),
              ],
              rows: [
                [
                  l("当前演奏者", "当前演奏者 / Current Avatar"),
                  l(
                    "选中的演奏者名称，用大号字显示。",
                    "The selected performer's name, in large type.",
                  ),
                  l(
                    "未选择演奏者文件 / 请先在参数设置页选择 .harpist 文件",
                    "未选择演奏者文件 / No avatar file selected / 请先在参数设置页选择 .harpist 文件 / Select .harpist File",
                  ),
                ],
                [
                  l("当前 MIDI", "当前 MIDI / Current MIDI"),
                  l(
                    "MIDI 文件名（不含扩展名，大号字）加完整路径（小字）。",
                    "The MIDI file name without extension (large) plus its full path (small).",
                  ),
                  l(
                    "未选择 MIDI 文件 / 请先在 MIDI 信息页完成文件选择与轨道筛选",
                    "未选择 MIDI 文件 / No MIDI file selected / 请先在 MIDI 信息页完成文件选择与轨道筛选",
                  ),
                ],
                [
                  l("选中轨道", "选中轨道 / Selected tracks"),
                  l(
                    "本次要生成动画的轨道号列表，形如 [2] 或 [2, 3]。",
                    "The list of track indices to animate, such as [2] or [2, 3].",
                  ),
                  l("显示空列表 []", "Shows an empty list []"),
                ],
                [
                  l("泛音音轨", "泛音音轨 / Harmonic tracks"),
                  l(
                    "被勾了 🎵 泛音 的轨道号列表。**只有存在泛音轨时这一行才出现。**",
                    "The track indices ticked as 🎵 泛音 / Harmonic. **This row only appears when harmonic tracks exist.**",
                  ),
                  l("整个一行都不显示", "The whole row is hidden"),
                ],
                [
                  l("帧率", "帧率 / FPS"),
                  l(
                    "当前 目标帧率:，形如 60 fps。",
                    "The current 目标帧率: / Target FPS: value, such as 60 fps.",
                  ),
                  l("不适用", "Not applicable"),
                ],
                [
                  l("记录池", "记录池 / Pool"),
                  l(
                    "当前 记录池大小: 的数值，形如 400。",
                    "The current 记录池大小: / Pool size: value, such as 400.",
                  ),
                  l("不适用", "Not applicable"),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              text: l(
                "**这一屏没有任何输入框**，看到 帧率 是 60 而你想改成 30，得回 参数设置 改；看到 泛音音轨 少了一条轨，得回 MIDI 信息 补勾。概览不会自动刷新以外的东西——它每帧都读当前状态，所以你改完回来立刻就是新值。",
                "**There is no input field on this screen.** If 帧率 / FPS shows 60 and you want 30, go back to 参数设置 / Settings; if 泛音音轨 / Harmonic tracks is missing a track, go back to MIDI 信息 / MIDI Info and tick it. The overview reads live state, so the values are already updated when you return.",
              ),
            },
          ],
        },
        {
          id: "execute-run",
          title: l(
            "执行操作：只有一键执行",
            "执行操作 / Actions — one-click only",
          ),
          blocks: [
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("作用", "Effect"),
                l("什么时候用", "When to use it"),
              ],
              rows: [
                [
                  l("一键执行 卡片", "一键执行 / Run All card"),
                  l(
                    "卡片副标题写着「串联全部阶段，MIDI → 竖琴演奏动画」。本产品**没有分步执行**，这是唯一一条执行路径。",
                    "The card subtitle reads “串联全部阶段，MIDI → 竖琴演奏动画”. This product has **no step-by-step execution** — this is the only path.",
                  ),
                  l(
                    "配置确认无误后直接出结果。",
                    "Once the configuration is confirmed, to get output.",
                  ),
                ],
                [
                  l("⚡ 生成动画", "⚡ 生成动画 / Generate Animation"),
                  l(
                    "按顺序跑完全部阶段：MIDI 预处理 → 踏板规划 → 音符处理 → 弦映射 → 指法与踏板序列搜索 → 动画生成与写文件。",
                    "Runs every stage in order: MIDI preprocessing → pedal planning → note processing → string mapping → fingering and pedal sequence search → animation generation and file writing.",
                  ),
                  l(
                    "运行期间按钮下方出现转圈与「正在执行...」，结束后自动消失。",
                    "A spinner and “正在执行... / Running...” appear below the button while it runs and disappear when it finishes.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l("点击后的校验顺序", "Validation order after clicking"),
              text: l(
                "按钮会先按固定顺序检查三项，任一项不满足就只往控制台写一条错误并**不启动流水线**：MIDI 文件 → 演奏者文件 → 音轨。对应的错误分别是 错误: 未选择 MIDI 文件、错误: 未选择演奏者文件、错误: 未选择任何音轨。所以看到的是第一条错误，不代表后面两项没问题。",
                "The button checks three things in a fixed order and, if any fails, writes just one error to the console and **does not start the pipeline**: MIDI file → performer file → tracks. The errors are 错误: 未选择 MIDI 文件 / No MIDI file selected, 错误: 未选择演奏者文件 / No avatar file selected and 错误: 未选择任何音轨 / Error: no track selected. Seeing the first one does not mean the later two are fine.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l("运行中不能取消", "You cannot cancel a run"),
              text: l(
                "没有取消按钮，要中断只能关掉程序。已经写出的中间文件会保留在 output/ 下，下次执行覆盖同名文件。想少等一会儿，回 参数设置 把 记录池大小: 调小。",
                "There is no cancel button — the only way to stop is to close the app. Intermediate files already written stay under output/ and are overwritten by the next run. To wait less, lower 记录池大小: / Pool size: on the 参数设置 / Settings tab.",
              ),
            },
          ],
        },
        {
          id: "execute-console",
          title: l(
            "控制台输出：怎么看日志",
            "控制台输出 / Console Output — reading the log",
          ),
          blocks: [
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("作用", "Effect"),
                l("结果", "Result"),
              ],
              rows: [
                [
                  l("📋 复制结果", "📋 复制结果 / Copy Result"),
                  l(
                    "把最近一次成功运行生成的 .harpglide 报告的**绝对路径**复制到剪贴板。",
                    "Copies the **absolute path** of the most recent successful .harpglide report to the clipboard.",
                  ),
                  l(
                    "成功日志区多一行「已复制报告路径: {}」；还没成功跑过时写一条 错误: 尚无运行结果可复制。",
                    "Adds “已复制报告路径: {}” to the log; before any successful run it writes 错误: 尚无运行结果可复制 / “Error: no run result available to copy”.",
                  ),
                ],
                [
                  l("复制全部", "复制全部 / Copy All"),
                  l(
                    "把整段控制台日志复制成纯文本，方便贴给作者排查。",
                    "Copies the entire console log as plain text, handy when asking the author for help.",
                  ),
                  l("没有额外提示。", "No extra message."),
                ],
                [
                  l("清空", "清空 / Clear"),
                  l(
                    "清空当前日志显示，不影响已生成的文件。",
                    "Clears the log view without touching generated files.",
                  ),
                  l(
                    "日志区回到「暂无日志输出」。",
                    "The log area returns to “暂无日志输出 / No logs yet”.",
                  ),
                ],
                [
                  l("日志 N 条", "日志 N 条"),
                  l(
                    "状态徽章：当前日志区里有多少行。",
                    "Status badge: how many lines the log currently holds.",
                  ),
                  l("实时更新。", "Updates live."),
                ],
                [
                  l("空闲 / 运行中", "空闲 / Idle / 运行中 / Running"),
                  l(
                    "状态徽章：程序当前是在待命还是在算。运行中显示在主题色底上。",
                    "Status badge: whether the app is idle or computing. While running it sits on the accent colour.",
                  ),
                  l(
                    "与按钮下方的转圈同步。",
                    "Keeps step with the spinner under the button.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "日志**按级别着色**，不靠关键字猜：普通信息是等宽灰字，成功是绿色，警告是黄色，错误是红色。一次成功运行大致按这个顺序铺开：=== 开始执行流水线 === → Stage 2 完成: {} 个音符 → Stage 3 完成: {} 次踏板变化 → Stage 4 完成: {} 个处理后音符 → Stage 5 完成: {} 个弦映射方案 → Stage 6 完成: 最优熵值 {} → Stage 7 的一串子步骤（左手 / 右手 / 左脚 / 右脚 / 弦振动 / 竖琴 / 头部逐项完成）→ Stage 7 完成: 生成 {} 个文件 → 各输出文件的路径 → 完成: 流水线成功，生成 {} 个文件 → 最优姿态序列: {} 帧 → === 流水线结束 ===。",
                "The log is **coloured by severity** rather than guessed from keywords: ordinary info is monospace grey, success green, warnings yellow, errors red. A successful run unfolds roughly in this order: === 开始执行流水线 === → Stage 2 完成: {} 个音符 → Stage 3 完成: {} 次踏板变化 → Stage 4 完成: {} 个处理后音符 → Stage 5 完成: {} 个弦映射方案 → Stage 6 完成: 最优熵值 {} → a chain of Stage 7 sub-steps (left hand, right hand, left foot, right foot, string vibration, harp, head) → Stage 7 完成: 生成 {} 个文件 → the paths of every output file → 完成: 流水线成功，生成 {} 个文件 → 最优姿态序列: {} 帧 → === 流水线结束 ===.",
              ),
            },
            {
              kind: "callout",
              tone: "tip",
              title: l(
                "什么时候该去改参数",
                "When to go back and tune something",
              ),
              text: l(
                "日志里出现成片的 [HandAssignment] 丢弃音符，或者搜索阶段的统计里「速度超标跳过帧数」「速度过滤淘汰候选总数」「池子变空次数」偏高，说明手速上限卡得太紧。处理顺序：先把 手部移动速度 (弦/秒) 调大，再把 记录池大小: 调大；仍然不行说明这段编曲同时发声的音符太多，需要简化 MIDI。",
                "Runs of [HandAssignment] 丢弃音符 in the log, or high “速度超标跳过帧数” / “速度过滤淘汰候选总数” / “池子变空次数” counts in the search statistics, mean the hand-speed ceiling is too tight. Fix in this order: raise 手部移动速度 (弦/秒) / Hand move speed (strings/s), then raise 记录池大小: / Pool size:. If it still fails, the arrangement simply has too many simultaneous notes and the MIDI needs simplifying.",
              ),
            },
            {
              kind: "callout",
              tone: "info",
              text: l(
                "日志里还可能看到「警告: 加载参考姿态失败，将在 Stage 7 前重试」。这只是初始化时没读到 .harpist 里的参考姿态，程序会在出动画之前再试一次；那次仍失败才会报出「左手参考姿态未加载」/「右手参考姿态未加载」并中止。切界面语言不会回溯翻译已经打印出来的旧日志。",
                "You may also see “警告: 加载参考姿态失败，将在 Stage 7 前重试”. That only means the reference poses in the .harpist were not read during startup; the app retries before generating animation, and only aborts with 「左手参考姿态未加载 / Not loaded」 / “Left hand reference poses not loaded” or 「右手参考姿态未加载 / Not loaded」 / “Right hand reference poses not loaded” if that retry fails too. Switching the UI language does not retranslate lines already printed.",
              ),
            },
          ],
        },
      ],
      image: { image: 3 },
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 4 / Poses 查看
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-poses",
      title: l(
        "页签 4：Poses 查看（可选）",
        "Tab 4 — Poses 查看 / Poses Viewer (optional)",
      ),
      blocks: [
        {
          kind: "p",
          text: l(
            "这是只读的调试页，标题是 Poses 查看器，副标题「浏览和搜索竖琴演奏手型数据」。它把搜索阶段算出的最优姿态序列摊成一张张卡片，让你按帧号、手指动作和左右手逐条检查。**它不参与生成**，只想出动画的话可以完全不打开。",
            "This is a read-only debugging tab, titled Poses 查看器 / Viewer with the subtitle “浏览和搜索竖琴演奏手型数据”. It lays the best pose sequence from the search out as cards so you can inspect it by frame, finger action and hand. **It takes no part in generation** — skip it entirely if you just want animation.",
          ),
        },
      ],
      subs: [
        {
          id: "poses-source",
          title: l("数据从哪来", "Where the data comes from"),
          blocks: [
            {
              kind: "p",
              text: l(
                "数据来源是固定的，不用你选文件：程序按**当前的 MIDI、已勾选的轨道、选中的演奏者**拼出 output/poses/{MIDI文件名}_{轨道号}_{演奏者名}_poses.json 并自动加载。所以打开这一页之前，前三个页签必须已经设置好，并且**至少完整生成过一次**——那个 JSON 是在搜索阶段写出来的。",
                "The source is fixed and you never choose a file: the app builds and auto-loads output/poses/{midi}_{track indices}_{performer}_poses.json from **the current MIDI, ticked tracks and selected performer**. So before opening this tab the first three tabs must be set up and you must have **completed at least one full run** — that JSON is written during the search stage.",
              ),
            },
            {
              kind: "table",
              head: [
                l("顶部信息行", "Header row"),
                l("显示什么", "Shows"),
                l("异常时", "When it is wrong"),
              ],
              rows: [
                [
                  l("文件:", "文件: / File:"),
                  l(
                    "当前加载的 _poses.json 完整路径。",
                    "The full path of the loaded _poses.json.",
                  ),
                  l("显示「未加载」。", "Shows “未加载 / Not loaded”."),
                ],
                [
                  l("记录数:", "记录数: / Records:"),
                  l(
                    "一共加载了多少条姿态记录。",
                    "How many pose records were loaded.",
                  ),
                  l("显示 —。", "Shows —."),
                ],
              ],
            },
            {
              kind: "table",
              head: [
                l("横幅", "Banner"),
                l("颜色", "Colour"),
                l("含义", "Meaning"),
              ],
              rows: [
                [
                  l(
                    "请先在 MIDI 面板选择 MIDI 文件和轨道，再切换到本面板",
                    "请先在 MIDI 面板选择 MIDI 文件和轨道，再切换到本面板 / Select a MIDI file and tracks on the MIDI panel first, then switch to this panel",
                  ),
                  l("蓝色（信息）", "Blue (info)"),
                  l(
                    "还没确定数据路径：MIDI 或轨道还没选。这不是错误，只是还没到时候。",
                    "The data path is not determined yet because the MIDI or tracks are unset. Not an error, just too early.",
                  ),
                ],
                [
                  l(
                    "无法加载 _poses.json 文件，请确认已经执行过生成",
                    "无法加载 _poses.json 文件，请确认已经执行过生成 / “Failed to load the _poses.json file; please make sure generation has been run”",
                  ),
                  l("红色（警告）", "Red (warning)"),
                  l(
                    "路径已经确定但读不到或解析不了。多数情况是还没跑过生成，或跑生成之后把 output/ 挪走了。控制台会写下 ❌ PosesReader: 读取文件失败 或 ❌ PosesReader: 解析 _poses.json 文件失败。",
                    "The path is known but the file cannot be read or parsed — usually because generation has not run yet, or output/ was moved afterwards. The console logs ❌ PosesReader: 读取文件失败 or ❌ PosesReader: 解析 _poses.json 文件失败.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "poses-search",
          title: l("搜索栏：五个筛选条件", "The search bar — five filters"),
          blocks: [
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("作用", "Effect"),
                l("范围 / 取值", "Range"),
                l("默认", "Default"),
                l("怎么用", "How to use it"),
              ],
              rows: [
                [
                  l("帧数:", "帧数: / Frame:"),
                  l(
                    "目标帧号。**它不是一个精确匹配条件**，而是搜索区间的中心。",
                    "The target frame. **It is not an exact match** — it is the centre of the search window.",
                  ),
                  l("数字，提示语「留空=全局」", "A number; hint “留空=全局”"),
                  l("留空（等于不限帧）", "Empty (no frame limit)"),
                  l(
                    "怀疑某一帧的手型不对，就把那个帧号填进来。",
                    "When a specific frame's hand shape looks wrong, type that frame number.",
                  ),
                ],
                [
                  l("±", "±"),
                  l(
                    "搜索半径：实际取的是「目标帧 ± 半径」这个区间里的全部记录。",
                    "The search radius: every record inside “target frame ± radius” is returned.",
                  ),
                  l("0 – 200，后缀显示为「帧」", "0 – 200, suffixed “帧”"),
                  l("10.0", "10.0"),
                  l(
                    "想看得宽一点就调大；半径 0 表示只取恰好落在该帧上的记录。",
                    "Widen it to see more; a radius of 0 returns only records exactly on that frame.",
                  ),
                ],
                [
                  l("动作筛选", "动作筛选 / Action filter"),
                  l(
                    "总开关。勾上之后才展开后面四个三态下拉和 手侧:。",
                    "The master switch: the four tri-state dropdowns and 手侧: / Hand: only appear once it is ticked.",
                  ),
                  l("勾选 / 不勾选", "On / off"),
                  l("不勾选", "Off"),
                  l(
                    "只想按手侧筛，可以不勾它——手侧是独立生效的。",
                    "To filter by hand alone you can leave it off — hand side works independently.",
                  ),
                ],
                [
                  l(
                    "拨弦 / 空闲 / 消音 / 泛音",
                    "拨弦 / Pluck / 空闲 / Idle / 消音 / Damp / 泛音 / Harmonic",
                  ),
                  l(
                    "四个三态下拉，分别对应手指的四种动作。每个都可以设成「要」「不要」或「不管」。",
                    "Four tri-state dropdowns, one per finger action. Each can be required, excluded, or ignored.",
                  ),
                  l("任意 / 是 / 否", "任意 / Any, 是 / Yes, 否 / No"),
                  l("全部 任意", "All 任意 / Any"),
                  l(
                    "**四个条件是「与」关系，而且必须落在同一根手指上**：某一只手里要有一根手指同时满足这四项，这条记录才会被留下。四个都设 任意 等于不过滤。",
                    "**The four conditions are AND-ed and must land on one finger**: a single finger on either hand must satisfy all four for the record to survive. Setting all four to 任意 / Any effectively disables the filter.",
                  ),
                ],
                [
                  l("手侧:", "手侧: / Hand:"),
                  l(
                    "只看有左手数据的、只看有右手数据的、或都看。它检查的是这条记录里有没有那只手的数据，与手指动作无关。",
                    "Show records with left-hand data, right-hand data, or both. It checks whether the record has that hand at all, independent of finger actions.",
                  ),
                  l(
                    "任意 / 左手 / 右手",
                    "任意 / Any, 左手 / Left hand, 右手 / Right hand",
                  ),
                  l("任意", "任意 / Any"),
                  l(
                    "排查单手问题时最省事：先按手侧收窄，再叠加动作条件。",
                    "The easiest way into a one-hand problem: narrow by hand first, then add action conditions.",
                  ),
                ],
                [
                  l("🔍 搜索", "🔍 搜索 / Search"),
                  l(
                    "按当前条件筛选并列出结果。",
                    "Filters by the current conditions and lists the results.",
                  ),
                  l("按钮", "Button"),
                  l("—", "—"),
                  l(
                    "**不点它不会出结果**。结果区在点之前会提示「共 N 条记录，输入帧数或启用手指标签进行搜索」。",
                    "**Nothing is listed until you press it.** Before that the results area says “共 N 条记录，输入帧数或启用手指标签进行搜索” / “{} records; enter a frame number or enable finger tags to search”.",
                  ),
                ],
                [
                  l("🔄 刷新", "🔄 刷新 / Refresh"),
                  l(
                    "丢掉已加载的数据，按当前的 MIDI / 轨道 / 演奏者重新加载 _poses.json，同时清空条件与结果。",
                    "Discards the loaded data, reloads _poses.json for the current MIDI / tracks / performer, and clears conditions and results.",
                  ),
                  l("按钮", "Button"),
                  l("—", "—"),
                  l(
                    "刚重新生成过一次动画之后点它，才能看到新的姿态数据。",
                    "Press it after regenerating animation to see the new pose data.",
                  ),
                ],
                [
                  l("✕ 清除", "✕ 清除 / Clear"),
                  l(
                    "把动作筛选开关、四个三态下拉、手侧全部恢复默认，并清空帧数与结果。",
                    "Resets the action-filter switch, the four tri-state dropdowns and hand side to defaults, and clears the frame box and results.",
                  ),
                  l("按钮", "Button"),
                  l("—", "—"),
                  l(
                    "条件越调越乱时一键回位。",
                    "One press to get back on track when conditions pile up.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "poses-cards",
          title: l("结果卡片怎么读", "Reading the result cards"),
          blocks: [
            {
              kind: "p",
              text: l(
                "每条结果是一张可点选的卡片，点一下即选中（边框变主题色、底色变浅）。卡片左侧是这一帧的摘要，右侧是手指表。超过 100 条匹配时顶部出现一行「匹配 {} 条，仅显示前 100 条」，并且只渲染前 100 条。",
                "Each result is a clickable card — one click selects it (accent border, tinted background). The left side is the frame summary, the right side is the finger table. Above 100 matches a line reads “匹配 {} 条，仅显示前 100 条” and only the first 100 are rendered.",
              ),
            },
            {
              kind: "table",
              head: [
                l("位置", "Where"),
                l("含义", "Meaning"),
                l("怎么用它排查", "How it helps"),
              ],
              rows: [
                [
                  l("帧 {N}", "帧 {N}"),
                  l(
                    "这条姿态对应的动画帧号。整数帧直接显示，非整数保留两位小数。",
                    "The animation frame this pose belongs to. Whole frames print as integers, others keep two decimals.",
                  ),
                  l(
                    "用它回到你的动画里定位问题时刻。",
                    "Use it to locate the exact moment in your animation.",
                  ),
                ],
                [
                  l("tick: {N}", "tick: {N}"),
                  l(
                    "同一帧在原始 MIDI 里的 tick 值。",
                    "The raw MIDI tick corresponding to that frame.",
                  ),
                  l(
                    "拿去和外部 MIDI 编辑器里的音符位置对照。",
                    "Cross-check it against note positions in an external MIDI editor.",
                  ),
                ],
                [
                  l(
                    "左手 / 右手 与 pos: {N}",
                    "左手 / Left hand / 右手 / Right hand with pos: {N}",
                  ),
                  l(
                    "这只手在弦面上的位置。只有该手参与这一帧时才显示；两只手都没有数据时卡片显示「无手部数据」。",
                    "That hand's position along the strings. Shown only when the hand takes part in the frame; with neither hand present the card says “无手部数据”.",
                  ),
                  l(
                    "pos 突然跳变的地方，就是手在弦面上大幅换位的地方。",
                    "A sudden jump in pos is where the hand makes a large shift across the strings.",
                  ),
                ],
                [
                  l("熵: {N}", "熵: {N}"),
                  l(
                    "累计熵：搜索用来衡量这套姿态序列有多「费力」的代价指标。数值越低越接近省力解。",
                    "The accumulated entropy: the search's cost metric for how effortful the pose sequence is. Lower is closer to the economical solution.",
                  ),
                  l(
                    "同一段音乐里熵持续偏高，说明这段指法排得很勉强，可以回 参数设置 调 记录池大小: 或 手部移动速度 (弦/秒)。",
                    "Persistently high entropy over a passage means the fingering is strained — go back to 参数设置 / Settings and raise 记录池大小: / Pool size: or 手部移动速度 (弦/秒) / Hand move speed (strings/s).",
                  ),
                ],
                [
                  l("手指表 指型", "Finger table 指型 / Finger"),
                  l(
                    "手指代号，只有四根：T 拇指、I 食指、M 中指、R 无名指。",
                    "The finger code — only four: T thumb, I index, M middle, R ring.",
                  ),
                  l(
                    "看某根手指是不是被用得太频繁，或整段都没动。",
                    "Check whether one finger is overworked or never moves.",
                  ),
                ],
                [
                  l("手指表 动作", "Finger table 动作 / Action"),
                  l(
                    "该手指在这一帧做什么：拨弦（绿）、空闲（灰）、消音（黄）、泛音（主题色）。",
                    "What the finger is doing this frame: 拨弦 / Pluck (green), 空闲 / Idle (grey), 消音 / Damp (yellow), 泛音 / Harmonic (accent).",
                  ),
                  l(
                    "本该拨弦的地方显示 空闲，说明这个音没被分到这只手。",
                    "An 空闲 / Idle where a pluck was expected means the note was not assigned to that hand.",
                  ),
                ],
                [
                  l("手指表 弦", "Finger table 弦 / Str"),
                  l(
                    "该手指的目标弦号，没有目标时显示 —。",
                    "The finger's target string index, or — when there is none.",
                  ),
                  l(
                    "手型看着别扭时，先看这里——弦号分错是最常见的原因。",
                    "When a hand shape looks wrong, look here first — a wrong string index is the most common cause.",
                  ),
                ],
                [
                  l("手指表 方向", "Finger table 方向 / Dir"),
                  l(
                    "拨弦方向：内 或 外（相对弦面）。",
                    "The pluck direction: 内 / In or 外 / Out, relative to the string plane.",
                  ),
                  l(
                    "方向反了会让手指看起来从琴弦背面穿过去。",
                    "A reversed direction makes the finger appear to pass through the back of the string.",
                  ),
                ],
              ],
            },
            {
              kind: "ul",
              items: [
                l(
                  "结果区标题 匹配结果: 后面跟匹配条数。",
                  "The 匹配结果: / Matches: heading is followed by the match count.",
                ),
                l(
                  "☐ 全选 / ✕ 取消全选：切换全部**已显示**条目的选中状态（所以上限是那 100 条）。",
                  "☐ 全选 / Select All / ✕ 取消全选 / Deselect All: toggles every **displayed** row (so at most those 100).",
                ),
                l(
                  "📋 复制结果：把选中的卡片导出成文本，日志给出「📋 已复制 {} 条记录到剪贴板」。没选中任何卡片时按钮不可点。",
                  "📋 复制结果 / Copy Result: exports the selected cards as text and logs “📋 已复制 / Copy {} 条记录到剪贴板”. The button is disabled with nothing selected.",
                ),
                l(
                  "复制出来的每条记录包含帧号、tick、左右手的 pos、每根手指的 指型 / 动作 / 弦 / 方向，最后是累计熵。把这段文字发给作者，比截图好对照得多。",
                  "Each copied block contains the frame, tick, the left/right pos, every finger's 指型 / 动作 / 弦 / 方向 / Dir, and the accumulated entropy. Sending that text to the author works far better than a screenshot.",
                ),
                l(
                  "没有匹配时显示「暂无匹配结果」；如果你填过条件，提示语会变成「没有找到匹配的记录，请调整搜索条件」。",
                  "With no matches the area shows “暂无匹配结果 / No matching results”; once conditions were set the hint becomes “没有找到匹配的记录，请调整搜索条件” / “No matching records; please adjust the search criteria”.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              title: l("三个典型用法", "Three typical uses"),
              text: l(
                "**手型不对**：把出问题的那一帧填进 帧数:、半径 ±10，勾上 动作筛选，看每根手指被分到了哪根弦、方向 有没有反。**泛音段落可疑**：把 泛音 设成 是、其余设 任意，只列出被判为泛音的姿态。**某只手整段没动**：用 手侧: 选那只手，再把 空闲 设成 是，看它是不是一路空闲。",
                "**Wrong hand shape**: put the offending frame into 帧数: / Frame: with a ±10 radius, tick 动作筛选 / Action filter, and check which string each finger got and whether 方向 / Dir is reversed. **Suspicious harmonic passage**: set 泛音 / Harmonic to 是 / Yes and the rest to 任意 / Any to list only poses judged harmonic. **One hand never moves**: pick it with 手侧: / Hand: and set 空闲 / Idle to 是 / Yes to see whether it stays idle throughout.",
              ),
            },
          ],
        },
      ],
      image: { image: 2 },
    },

    // ═════════════════════════════════════════════════════════════
    {
      id: "defaults",
      title: l(
        "哪些参数第一次用不用碰",
        "What you can safely leave alone at first",
      ),
      blocks: [
        {
          kind: "p",
          text: l(
            "下面这张表把全部可调项按「第一次用要不要动」分了类。结论是：跑通流程只需要动三处——选演奏者、勾轨道、必要时改 音符量化；其余全保持默认。",
            "The table splits every adjustable item by whether a first run needs it. The conclusion: getting a run working needs only three actions — pick a performer, tick tracks, and change 音符量化 / Note Quantization if needed. Everything else stays at its default.",
          ),
        },
        {
          kind: "table",
          head: [
            l("参数", "Parameter"),
            l("所在页签", "Tab"),
            l("默认值", "Default"),
            l("第一次用", "First run"),
            l("什么时候才需要动", "When you would need it"),
          ],
          rows: [
            [
              l("演奏者下拉菜单", "Performer dropdown"),
              l("参数设置", "参数设置 / Settings"),
              l("列表第一项", "First in the list"),
              l("**必选**", "**Required**"),
              l(
                "换角色，或列表为空时先新增一个。",
                "Switching characters, or adding one when the list is empty.",
              ),
            ],
            [
              l("记录池大小:", "记录池大小: / Pool size:"),
              l("参数设置", "参数设置 / Settings"),
              l("400", "400"),
              l("保持默认", "Keep"),
              l(
                "手型别扭（调大到 600–800）或嫌慢（调小）时。",
                "When hand shapes look awkward (raise to 600–800) or it feels slow (lower it).",
              ),
            ],
            [
              l("目标帧率:", "目标帧率: / Target FPS:"),
              l("参数设置", "参数设置 / Settings"),
              l("60.0 fps", "60.0 fps"),
              l("保持默认", "Keep"),
              l(
                "目标工程不是 60 fps 时，改成工程帧率。",
                "When your target project is not 60 fps — set it to match.",
              ),
            ],
            [
              l("启用滑奏检测", "启用滑奏检测 / Enable glissando detection"),
              l("参数设置", "参数设置 / Settings"),
              l("勾选（开启）", "On"),
              l("保持默认", "Keep"),
              l(
                "怀疑某段滑奏被判错时，取消勾选对比一次。",
                "When a glissando passage looks misjudged — untick and compare once.",
              ),
            ],
            [
              l(
                "动作参数 七个滑块",
                "The seven 动作参数 / Motion Parameters sliders",
              ),
              l("参数设置", "参数设置 / Settings"),
              l("见上一节表格", "See the table above"),
              l("保持默认", "Keep"),
              l(
                "动作太快 / 太慢、手够不到弦、琴体抖动、头部不动等具体现象出现时。",
                "When a specific symptom appears: motion too fast or slow, hands unable to reach, the harp jittering, the head frozen.",
              ),
            ],
            [
              l(
                "五状态偏移比例 四个滑块",
                "The four 五状态偏移比例 / Five-State Offset Ratios sliders",
              ),
              l("参数设置", "参数设置 / Settings"),
              l("5.0 / 3.0 / 4.0 / 10.0‰", "5.0 / 3.0 / 4.0 / 10.0‰"),
              l("保持默认", "Keep"),
              l(
                "手指看起来贴在弦上、拨弦没「吃进弦里」、拨完不收手等近距离观感问题时。",
                "For close-up feel problems: fingers glued to the string, a pluck that never bites, no follow-through.",
              ),
            ],
            [
              l("音符量化", "音符量化 / Note Quantization"),
              l("MIDI 信息", "MIDI 信息 / MIDI Info"),
              l("无量化", "无量化 / None"),
              l("**可能需要动**", "**May need changing**"),
              l(
                "素材是真人弹奏、时间点毛躁时，改 1/16 或 1/32。",
                "For human-played material with loose timing, use 1/16 or 1/32.",
              ),
            ],
            [
              l("轨道勾选", "Track checkboxes"),
              l("MIDI 信息", "MIDI 信息 / MIDI Info"),
              l("默认全部不勾", "Nothing ticked"),
              l("**必选**", "**Required**"),
              l(
                "换曲子、换声部时重新勾。",
                "Re-tick whenever you switch pieces or parts.",
              ),
            ],
            [
              l("🎵 泛音", "🎵 泛音 / Harmonic"),
              l("MIDI 信息", "MIDI 信息 / MIDI Info"),
              l("不勾选", "Off"),
              l("保持默认", "Keep"),
              l(
                "整条轨都是泛音声部时才勾。",
                "Only when a whole track is a harmonic part.",
              ),
            ],
            [
              l(
                "Poses 查看的全部筛选条件",
                "Every Poses 查看 / Poses Viewer filter",
              ),
              l("Poses 查看", "Poses 查看 / Poses Viewer"),
              l("不限帧、不筛选", "No frame limit, no filters"),
              l("不用管", "Ignore"),
              l(
                "只有排查手型、检查泛音段落时才会用到。",
                "Only for investigating hand shapes and harmonic passages.",
              ),
            ],
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    {
      id: "faq",
      title: l("参数常见疑问", "Parameter FAQ"),
      blocks: [
        {
          kind: "p",
          text: l(
            "**改了参数为什么没反应？** 参数只在你点 ⚡ 生成动画 时被读取一次。改完 参数设置 里的滑块，必须重新生成才会体现在动画里；Poses 查看 里看到的也还是上一次生成的结果。",
            "**I changed a parameter and nothing happened.** Parameters are read once, when you press ⚡ 生成动画 / Generate Animation. After moving a slider you must regenerate for it to take effect — and Poses 查看 / Poses Viewer still shows the previous run's result.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**记录池大小: 和 手部移动速度 (弦/秒) 到底先调哪个？** 先调手速。手速上限决定「哪些候选方案合法」，池子宽度决定「在合法的里面挑得多细」。手速卡太死时池子再大也没用，所以顺序是先 手部移动速度 (弦/秒)、后 记录池大小:。",
            "**Which comes first, 记录池大小: / Pool size: or 手部移动速度 (弦/秒) / Hand move speed (strings/s)?** Speed first. The speed ceiling decides which candidates are legal; the pool width decides how carefully you pick among the legal ones. A too-tight speed ceiling makes pool width irrelevant, so raise 手部移动速度 (弦/秒) / Hand move speed (strings/s) first, then 记录池大小: / Pool size:.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**转向敏感度 (%) 和 头部运动敏感度 (%) 都是百分比，是不是越高越好？** 不是。100% 意味着「只要有变化就重算 / 就记关键帧」，结果是竖琴一直抖、头部曲线毛躁，而且计算量最大。它们的作用是**过滤掉小抖动**，所以遇到动作太碎应该往下调。",
            "**Both 转向敏感度 (%) / Turning sensitivity (%) and 头部运动敏感度 (%) / Head motion sensitivity (%) are percentages — is higher better?** No. 100% means “recompute / record on any change at all”, which makes the harp jitter continuously and the head curve noisy, at the highest computation cost. Their job is **filtering out tiny jitter**, so lower them when the motion looks twitchy.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**五状态偏移比例 调大了，手指会不会真的偏离琴弦？** 会，但幅度很小：偏移是比例乘上音符时长、再乘 0 号弦长度得到的，而且只作用在弦附近的准备、保持、释放动作上。如果手指整体落错了弦或整只手位置不对，那是 .harpist 里的手位问题，调这里没用。",
            "**If I raise the 五状态偏移比例 / Five-State Offset Ratios values, will the fingers actually leave the string?** Yes, but slightly: the offset is the ratio scaled by note duration and by the length of string 0, and it only affects the ready / hold / release motion near the string. If a finger lands on the wrong string, or the whole hand sits wrongly, that is a hand-position problem in the .harpist — these sliders will not fix it.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**为什么 MIDI 信息 卡片有时候不出现？** 它只在扫描出速度变化、或 Ticks/Beat 不等于内置初始值 480 时显示。你的文件既没有速度变化又是每拍 480 tick，它就隐藏——这属于正常现象，不是扫描失败。",
            "**Why does the MIDI 信息 / MIDI Info card sometimes not appear?** It is shown only when the scan found tempo changes, or when Ticks/Beat differs from the built-in initial value 480. A file with neither is regular enough that it stays hidden — normal, not a failed scan.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**🎵 泛音 勾了之后，输出文件名有什么变化？** 中间阶段临时文件的名字里会多出 _harmonic_{轨道号} 这一段，方便你确认泛音标记确实生效了；最终报告的命名规则不变。执行页的 配置概览 同时会多出一行 泛音音轨。",
            "**What changes in the file names once I tick 🎵 泛音 / Harmonic?** The intermediate temp files gain a _harmonic_{track} segment so you can confirm the flag took effect; the final report's naming is unchanged. The execution page's 配置概览 / Configuration Overview also gains a 泛音音轨 / Harmonic tracks row.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**关掉程序再打开，我调过的参数还在吗？** 不在。参数是内存里的状态，重启回到默认值；但**演奏者列表是持久化的**（列表存在 assets/harpist/avatars.json，保存演奏者时会重写它）。界面语言也是持久化的，另存在配置文件里。",
            "**Are my parameter changes still there after restarting the app?** No. Parameters live in memory and reset on start. The **performer list is persistent**, though — it lives in assets/harpist/avatars.json and is rewritten whenever you save a performer. The UI language is persistent too, stored in a config file.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**界面上的参数在英文版里叫什么？** 位置完全一样，只是标签是英文：参数设置 是 Settings、动作参数 是 Motion Parameters、五状态偏移比例 是 Five-State Offset Ratios、技法检测 是 Technique Detection，记录池大小: 与 目标帧率: 是 Pool size: 与 Target FPS:。",
            "**What are these parameters called in the English build?** Same positions, English labels: 参数设置 / Settings is Settings, 动作参数 / Motion Parameters is Motion Parameters, 五状态偏移比例 / Five-State Offset Ratios is Five-State Offset Ratios, 技法检测 / Technique Detection is Technique Detection, and 记录池大小: / Pool size: / 目标帧率: / Target FPS: are Pool size: / Target FPS:.",
          ),
        },
      ],
    },
  ],
};
